import PaginatedTable from '../../components/ui/PaginatedTable';
import PermissionButton from "../../components/ui/PermissionButton";
import FinancialScopeNotice from '../../components/ui/FinancialScopeNotice';
import { useEffect, useMemo, useState } from "react";
import DashboardLayout from "../Layouts/DashboardLayout";
import ProtectedRoute from "../Components/ProtectedRoute";
import {
  Download,
  Printer,
  Plus,
  Search,
  Wallet,
  ArrowDownCircle,
  ArrowUpCircle,
} from "lucide-react";
import CashTransactionViewModal from "./CashTransactionViewModal";

export default function CashBook() {
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState({
    opening: 0,
    cash_in: 0,
    cash_out: 0,
    closing: 0,
  });
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    loadCashBook();
  }, []);

  async function loadCashBook() {
    const res = await fetch("/api/cash-book", {
      credentials: "include",
    });

    const data = await res.json();

    if (data.status === "success") {
      setTransactions(data.transactions);
      setSummary(data.summary);
    }
  }

  const filtered = useMemo(() => {
    return transactions.filter((t) => {
      return (
        (t.description || "").toLowerCase().includes(search.toLowerCase()) ||
        (t.reference || "").toLowerCase().includes(search.toLowerCase()) ||
        (t.voucher || "").toLowerCase().includes(search.toLowerCase())
      );
    });
  }, [transactions, search]);

  const totalIn = summary.cash_in;
  const totalOut = summary.cash_out;
  const closingBalance = summary.closing;

  return (
    <ProtectedRoute permission="accounting.cash_book">
      <DashboardLayout>
      <FinancialScopeNotice/>
        <div className="space-y-8">
          {/* Header */}

          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-serif">Cash Book</h1>

              <p className="text-sm text-gray-400 mt-1">
                Cash Receipts & Payments
              </p>
            </div>

            <div className="flex gap-3">
              <div className="relative">
                <Search
                  size={16}
                  className="absolute left-3 top-3 text-gray-500"
                />

                <input
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 pr-4 py-2 rounded-xl bg-white/5 border border-white/10"
                />
              </div>

              <PermissionButton permissionModule="accounts" className="px-5 py-2 rounded-xl bg-white/5 border border-white/10 flex gap-2 items-center">
                <Printer size={16} />
                Print
              </PermissionButton>

              <PermissionButton permissionModule="accounts" className="px-5 py-2 rounded-xl bg-white/5 border border-white/10 flex gap-2 items-center">
                <Download size={16} />
                Export
              </PermissionButton>
            </div>
          </div>

          {/* KPI */}

          <div className="grid md:grid-cols-4 gap-6">
            <KPI
              title="Cash Balance"
              value={closingBalance}
              color="text-amber-400"
              icon={Wallet}
            />

            <KPI
              title="Cash In"
              value={totalIn}
              color="text-emerald-400"
              icon={ArrowDownCircle}
            />

            <KPI
              title="Cash Out"
              value={totalOut}
              color="text-rose-400"
              icon={ArrowUpCircle}
            />

            <KPI
              title="Net Cash"
              value={totalIn - totalOut}
              color="text-indigo-400"
              icon={Wallet}
            />
          </div>

          {/* Summary */}

          <div className="grid md:grid-cols-3 gap-6">
            <SummaryCard
              title="Cash In"
              value={`₹${Number(summary.cash_in).toLocaleString("en-IN")}`}
            />

            <SummaryCard
              title="Cash Out"
              value={`₹${Number(summary.cash_out).toLocaleString("en-IN")}`}
            />

            <SummaryCard
              title="Opening Balance"
              value={`₹${Number(summary.opening).toLocaleString("en-IN")}`}
            />
          </div>

          {/* Table */}

          <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
            <PaginatedTable className="w-full">
              <thead className="bg-black/40 text-gray-400">
                <tr>
                  <th className="text-left px-6 py-4">Date</th>

                  <th className="text-left">Voucher</th>

                  <th className="text-left">Category</th>

                  <th className="text-left">Description</th>

                  <th className="text-right">Receipt</th>

                  <th className="text-right">Payment</th>

                  <th className="text-right pr-6">Balance</th>
                </tr>
              </thead>

              <tbody>
                {filtered.map((t) => (
                  <tr
                    key={t.id}
                    onClick={() => setSelected(t)}
                    className="border-t border-white/10 hover:bg-white/5 cursor-pointer transition"
                  >
                    <td className="px-6 py-4">{t.date}</td>

                    <td>{t.voucher}</td>

                    <td>{t.reference || "-"}</td>

                    <td>{t.narration || "-"}</td>

                    <td className="text-right text-emerald-400">
                      {Number(t.debit) > 0
                        ? `₹${Number(t.debit).toLocaleString("en-IN")}`
                        : "-"}
                    </td>

                    <td className="text-right text-rose-400">
                      {Number(t.credit) > 0
                        ? `₹${Number(t.credit).toLocaleString("en-IN")}`
                        : "-"}
                    </td>

                    <td className="text-right pr-6 text-amber-400 font-semibold">
                      ₹{Number(t.balance).toLocaleString("en-IN")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </PaginatedTable>
          </div>
        </div>

        {selected && (
          <CashTransactionViewModal
            transaction={selected}
            onClose={() => setSelected(null)}
          />
        )}
      </DashboardLayout>
    </ProtectedRoute>
  );
}

function KPI({ title, value, color, icon: Icon }) {
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
      <div className="flex justify-between items-center">
        <p className="text-xs uppercase tracking-widest text-gray-400">
          {title}
        </p>

        <Icon size={18} />
      </div>

      <h2 className={`mt-4 text-3xl font-serif ${color}`}>
        ₹{Number(value).toLocaleString()}
      </h2>
    </div>
  );
}

function SummaryCard({ title, value }) {
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
      <p className="text-xs uppercase tracking-widest text-gray-400">{title}</p>

      <h2 className="mt-4 text-2xl font-serif text-white">{value}</h2>
    </div>
  );
}
